export default {
    title: 'Team objectives',
    subtitle: "Let's achieve our vision",
    searchEmployee: 'Search an employee',
    createObjective: 'Create an objective',
    disabled: {
        title: 'Feature not enabled',
        description: 'This feature is not enabled for your organization.',
    },
    errors: {
        loading: 'Error loading objectives',
    },
    period: {
        label: 'Select the objective period',
        // A period keeps the objectives whose dates overlap it, attached to it or not: two periods
        // that overlap therefore show the same objectives, and without this sentence the picker
        // looks as though it does nothing.
        help: {
            label: 'About choosing a period',
            body: 'A period shows the objectives running during its dates, even when they belong to another period. A yearly objective therefore also appears when you look at a half-year.',
        },
        none: 'No objective period',
        allPeriods: 'All periods',
        customDates: 'Custom dates',
        customDatesDescription: 'Choose a start and end date',
    },
    reminders: {
        label: 'Send a reminder if the objective is not complete',
        twoMonthsBeforeEnd: 'Two months before the end',
        oneMonthBeforeEnd: 'One month before the end',
        twoWeeksBeforeEnd: 'Two weeks before the end',
        needsEndDate: "Choose the objective's dates to schedule a reminder.",
        datePassed: 'A reminder whose date has already passed can no longer be scheduled.',
    },
    objectivePeriodsButton: 'Objective periods',
    // Shared by the detail page's header and the form's period fields, so both read the exact
    // same phrasing for a date range.
    periodRange: 'From {{start}} to {{end}}',
    // Detail page header only, when the objective is filed under a period: the period's name,
    // then its full date range, so the objective can be placed in its period at a glance.
    periodWithCycle: '{{cycle}} • from {{start}} to {{end}}',
    pillars: {
        emptyState: {
            title: 'Set your strategic pillars',
            description:
                "Pillars group objectives under your company's priorities, so everyone can follow how each one is progressing.",
        },
        // What assistive technology announces in place of the tiles while they are being read; on
        // screen the row shows grey blocks, which say nothing on their own.
        loading: 'Loading the pillars',
        defineInVisionValues: 'Define the pillars in Vision and values',
        scrollPrevious: 'Previous pillars',
        scrollNext: 'Next pillars',
        progression: 'Progression',
        participantsLabel_one: 'Participant',
        participantsLabel_other: 'Participants',
        objectivesCount_one: '{{count}} objective',
        objectivesCount_other: '{{count}} objectives',
        participantsCount_one: '{{count}} participant',
        participantsCount_other: '{{count}} participants',
        progressAria: '{{name}} progression: {{value}}%',
        progressionValue: '{{value}}% Progress',
    },
    byOwner: {
        noObjectives: 'No objective for this period.',
        noObjectivesWithinReach:
            'No objective you can see in this period. The pillars above count the whole organization.',
        noResults: 'No employee matches your search.',
        noFilterResults: 'No employee matches the selected filters.',
        noFilterResultsInTeam: 'No employee on your team matches the selected filters.',
        noStatusResults: 'No objective is in the selected states.',
        unknownOwner: 'No owner',
        objectiveCount_one: '{{count}} objective',
        objectiveCount_other: '{{count}} objectives',
    },
    filters: {
        status: 'Objective status',
        onTrack: 'On track',
    },
    groups: {
        cycle: 'Period: {{label}}',
    },
    card: {
        dueDate: 'Due: {{date}}',
        overdue: 'Overdue',
        draftLinkLabel: '{{title}} (draft)',
        atRisk: 'At risk',
        collective: 'Collective',
        contributorsLabel: 'Contributors:',
        keyResults: 'Key results',
        keyResultPercentage: '{{percentage}}%',
        progressAria: 'Objective progression: {{value}}%',
        moreOptions: 'More options',
        edit: 'Edit',
        delete: 'Delete',
    },
    contributors: {
        label_one: 'Contributor',
        label_other: 'Contributors',
        you: '{{name}} (you)',
        count_one: '{{count}} contributor',
        count_other: '{{count}} contributors',
        openList_one: 'See the contributor',
        openList_other: 'See the {{count}} contributors',
        dialogTitle: 'Contributors ({{count}})',
    },
    delete: {
        title: 'Delete this objective?',
        message:
            'This action cannot be undone. The objective "{{title}}" and its key results will be deleted.',
        sharedMessage:
            'This action cannot be undone. The objective "{{title}}" is shared: it will be deleted for everyone who takes part in it, along with its key results.',
        cancel: 'Cancel',
        confirm: 'Delete',
        success: 'Objective deleted.',
    },
    pillarPage: {
        back: 'Back to objectives',
        strategicPillar: 'Strategic pillar',
        overallProgress: 'Overall pillar progress',
        peopleTitle: 'Who is driving this pillar',
        peopleSubtitle: "The objectives fuelling this pillar's progress",
        empty: 'No objective linked to this pillar in this period.',
        // What a reader whose reach stops at their own and their team's objectives sees: the pillar
        // may well be moving forward without them.
        emptyWithinReach: 'No objective in this pillar that you can see in this period.',
        emptyForTeam: 'No objective of your direct team serves this pillar in this period.',
        loading: 'Loading the pillar',
        notFound: 'Pillar not found.',
        // Under "Organization" the header counts the whole company and the list what the reader
        // may open: the sentence says why the list holds fewer.
        partialList_one:
            "You do not see this pillar's objective: it belongs to someone whose objectives you do not follow.",
        partialList_other:
            'You see {{visible}} of the {{count}} objectives of this pillar: the others belong to people whose objectives you do not follow.',
    },
    pillarScope: {
        label: 'Pillar scope',
        resolving: 'Loading the pillar scope',
        organization: 'Organization',
        filters: 'Selected filters',
        team: 'My team',
        pillarsMention: {
            organization: 'You are viewing these pillars for the whole organization.',
            filters: 'You are viewing these pillars based on the selected filters.',
            filtersForTeam_zero:
                'You are viewing these pillars based on the selected filters for the employees on your team',
            filtersForTeam_one:
                'You are viewing these pillars based on the selected filters for the employee on your team',
            filtersForTeam_other:
                'You are viewing these pillars based on the selected filters for the employees on your team',
            team: 'You are viewing these pillars and objectives for you and your direct team.',
        },
        pillarMention: {
            organization: 'You are viewing this pillar for the whole organization.',
            filters: 'You are viewing this pillar based on the selected filters.',
            filtersForTeam_zero:
                'You are viewing this pillar based on the selected filters for the employees on your team',
            filtersForTeam_one:
                'You are viewing this pillar based on the selected filters for the employee on your team',
            filtersForTeam_other:
                'You are viewing this pillar based on the selected filters for the employees on your team',
            team: 'You are viewing this pillar for you and your direct team.',
        },
        draftsCountNowhere: 'Drafts do not count toward any pillar.',
        filteredTeam: {
            count: '({{count}})',
            openList_one: '{{count}} employee: see the list',
            openList_other: '{{count}} employees: see the list',
            title: 'Employees on your team based on the selected filters',
            subtitle_one: '{{count}} employee',
            subtitle_other: '{{count}} employees',
            openProfile: 'Open the performance profile for {{name}}',
        },
    },
    type: {
        title: 'What type of objective would you like to create?',
        subtitle: 'Choose the type that best fits your needs',
        back: 'Go back',
        individual: 'Individual objective',
        individualDescription: 'For one employee',
        team: 'Team objective',
        teamDescription: 'Colleagues share a common objective and work together to achieve it',
    },
    form: {
        titleRequired: 'The objective title is required',
        titleMaxLength: 'The title must not exceed {{max}} characters.',
        holderRequired: 'Choose who this objective is for',
        holdersOutOfReach:
            'Choose at least one person from your team: once published, you could no longer see this objective.',
        dateRequired: 'This date is required',
        startAfterEnd: 'The start date must precede the end date',
        endBeforeStart: 'The end date must follow the start date',
        keyResultRequired: 'Add at least one key result',
        targetDifferentFromStart: 'The target must be different from the start value',
        keyResultTitleRequired: 'The key result name is required',
        valueOutOfRange: 'This value is out of range',
        selectEmployee: 'Select an employee',
        selectEmployees: 'Select employees',
        noEmployeesFound: 'No employee found',
        removeEmployee: 'Remove this employee',
        ownerKept:
            'An objective must keep at least one person from your team. To hand it over entirely to other people, ask someone with access to all employees, such as Human Resources.',
        participantPicker: {
            groups: {
                employees: 'Employees',
                departments: 'Departments',
                jobTitles: 'Job titles',
                locations: 'Locations',
                employeeLevels: 'Levels',
                managers: 'Team of',
            },
            managerEmployees: "{{name}}'s team",
            memberCount_one: '{{count}} person',
            memberCount_other: '{{count}} people',
            removeAttribute: 'Remove {{name}}',
            removeManagerEmployees: "Remove {{name}}'s team",
            unavailableAttribute: 'Unavailable value',
            attributeLoadFailed: 'Value could not be loaded',
            attributesLoadError: "The company's attributes could not be loaded.",
            noEmployeeIn: {
                department: 'You have no employees in the {{name}} department.',
                jobTitle: 'You have no employees with the {{name}} job title.',
                location: 'You have no employees at the {{name}} location.',
                employeeLevel: 'You have no employees at the {{name}} level.',
                team: "You have no employees in {{name}}'s team.",
                customValue: 'You have no employees with the {{name}} value.',
            },
        },
        teamTipBadge: 'Team objective',
        teamTipText:
            'To build an effective team objective, pair people whose contributions truly complement each other. You can then assign owners to the objectives to share out the roles and make sure every member plays a clear part in the collective success.',
        pillarSelectHelp: 'Select the strategic pillar you will be working towards.',
        pillarSelectHelpMultiple: 'Select the strategic pillars you will be working towards.',
        pillarSelectHelpRemoveOnly:
            'Your company allows one pillar per objective: you can remove pillars, not add any.',
        noPillarOffered: 'No active strategic pillar to choose from.',
        noPillar: 'No pillar',
        pillarChoiceRequired: 'Choose a strategic pillar or "No pillar".',
        pillarRequired: 'Choose a strategic pillar.',
        pillarRequiredNoneActive:
            'Your organization requires a strategic pillar, but none is active: the objective can be saved as a draft.',
        pillarsDeactivatedSinceRemoved_one:
            'The pillar {{names}} was deactivated after the draft was saved: it has been removed from the objective.',
        pillarsDeactivatedSinceRemoved_other:
            'The pillars {{names}} were deactivated after the draft was saved: they have been removed from the objective.',
        pillarsLoadError: 'The strategic pillars could not be loaded.',
        keepInMind: 'Keep in mind',
        keepInMindDesc:
            'As a leader, we strongly encourage you to align your objectives with the strategic pillars that drive our organizational vision.',
        keepInMindDescOwn:
            'We strongly encourage you to align your objectives with the strategic pillars that drive our organizational vision.',
        companyVision: 'Company vision',
        strategicAlignment: 'Strategic alignment',
        manage: 'Manage',
        managePillars: 'Manage the strategic pillars',
        showDescription: 'Show description',
        startDate: 'Start date',
        endDate: 'End date',
        title: 'Objective title',
        titlePlaceholder: 'Enter the objective title',
        // Deliberately the same string as `titlePlaceholder`: English has no second phrasing for
        // this invitation, unlike French, which distinguishes "Entrer..." (the field's own
        // placeholder) from "Inscrire..." (the button inviting the user back up to it).
        enterTitle: 'Enter the objective title',
        description: 'Description',
        descriptionPlaceholder: 'Add a description',
        competencies: 'Competencies worked on',
        selectEmployeeFirst: 'Select an employee first',
        noCompetencies: "No competency in this employee's profile",
        measurementElements: 'Key results',
        measurementElementsHelp:
            "Update the current value of each key result to track the objective's progress.",
        mainObjective: 'Objective',
        keyResultNamePlaceholder: 'Key result name',
        addMeasurementElement: 'Add a key result',
        removeKeyResult: 'Remove this key result',
        responsible: 'Responsible',
        responsibleOf: 'Responsible: {{name}}',
        noResponsible: 'No one responsible',
        startValue: 'Start',
        targetValue: 'Target',
        currentValue: 'Current value',
        yes: 'YES',
        no: 'NO',
        increaseValue: 'Increase the value',
        decreaseValue: 'Decrease the value',
        currencySymbol: 'Currency',
        unit: {
            number: 'Number',
            percentage: 'Percentage',
            monetary: 'Monetary',
            yesNo: 'Yes/No',
        },
        unitLabel: 'Type',
        selectUnit: 'Choose a type',
        selectPeriod: 'Choose a period',
        cancel: 'Cancel',
        saveDraft: 'Save the draft',
        publish: 'Publish the objective',
        saveChanges: 'Save the changes',
        draftSaved: 'Draft saved.',
        published: 'Objective published.',
        updated: 'Objective updated.',
        periodNotActive: 'The period is not active.',
        invalidForm: 'The form contains errors. Check the fields.',
        generateWithAi: 'Generate with AI',
        generateWithAiComingSoon: 'AI generation will come in a later release.',
        ai: {
            modalTitle: 'Generate an objective with AI',
            imageAlt: 'An objective taking shape',
            intentLabel: 'What do you want your employee to accomplish?',
            intentPlaceholder:
                'e.g. Improve customer satisfaction for tech support by the end of the quarter...',
            hint: 'The more specific your intent, the better the draft. Nothing is applied before you review it.',
            generate: 'Generate',
            generating: 'Generating',
            generatingMessage: 'Amélio AI is drafting your objective. This can take a moment.',
            regenerate: 'Start over',
            apply: 'Use this draft',
            cancel: 'Cancel',
            close: 'Close',
            previewTitle: 'Here is a draft',
            previewHint:
                'Review it, then apply it to the form where you can still edit everything.',
            resultTitle: 'Title',
            resultDescription: 'Description',
            resultKeyResults: 'Suggested key results',
            keyResultsKept: 'Your existing key results are kept; the suggestions are not applied.',
            keyResultRange: '{{start}} to {{target}}',
            error: 'The draft could not be generated. Please try again.',
            success: 'Draft applied to the form.',
            languageLabel: 'Write the draft in',
            languageFr: 'French',
            languageEn: 'English',
            limitReached:
                'You have used your {{limit}} drafts for today. The allowance opens again tomorrow.',
        },
    },
    detail: {
        back: 'Back',
        edit: 'Edit objective',
        description: 'The objective',
        noDescription: 'No description.',
        pillar: 'Strategic pillar',
        competencies: 'Competencies worked on',
        progressSaved: 'Progress saved.',
        notFound: 'Objective not found.',
        overallProgress: 'Overall progress',
        achieved: 'Objective achieved',
        skillsAndPillars: 'Competencies and strategic pillars',
        yourImpact: 'Your impact',
        impactDesc_one:
            'By working towards this goal, you are directly contributing to this strategic pillar and actively participating in the realization of our company vision.',
        impactDesc_other:
            'By working towards this goal, you are directly contributing to these strategic pillars and actively participating in the realization of our company vision.',
        strategicPillars: 'Strategic pillars',
        pillarModal: {
            sharedVision: 'Our shared vision',
            visionQuote: '“{{vision}}”',
            thisPillar: 'This strategic pillar',
            understood: 'Got it, thanks!',
        },
        discussion: {
            title: 'Discussion',
            subtitle: 'Discuss this objective with your team',
            empty: 'No comments yet. Start the conversation by posting an update.',
            placeholder: 'Write a comment…',
            publish: 'Publish',
            published: 'Comment published.',
            notifyCollaborators:
                'Notify the contributors of this objective and their {{managerLabel}}',
            notifyHelp: {
                label: 'About the email that goes out',
                body: 'The contributors and their {{managerLabel}} get an email as soon as the comment is posted. When unchecked, no email is sent, but the comment stays visible.',
            },
            loadError: 'The discussion could not be loaded.',
            unknownAuthor: 'Unknown author',
            filterLabel: 'Show in the discussion',
            filterAll: 'All',
            filterComments: 'Comments',
            filterEmpty: 'No comments yet, only automatic updates.',
            journal: {
                moved: 'moved “{{title}}” from {{from}} to {{to}}',
                entryLabel: 'Key result update',
                structure: {
                    entryLabel: 'Objective edit',
                    edited: 'edited the objective',
                },
            },
        },
    },
};
